import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { AxiosInstance, AxiosResponse } from "axios"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { HandledApiError } from "api/queryClient"
import {
  getProjects,
  getProjectInfo,
  availableArchivedProjects,
  getProjectFolders,
  getProjectType,
  projectMembers,
} from "api/Repos/projects"
import {
  PROJECT_FILTER,
  PROJECT_TYPE,
  ProjectData,
  ProjectListResponse,
  ArchivedProjectsResponse,
  GetProjectInfoResponse,
} from "types/project.types"
import handleSequenceError from "utils/handleSequenceError"

/**
 * React-query queries for the Projects domain (mutations live in
 * `useProjectsMutations.ts`).
 *
 * Queries gate their results through `handleSequenceError` — a sequence error
 * notifies the user there and throws `HandledApiError` so the global
 * QueryCache onError does not notify a second time.
 */

/** Thrown when the backend returns an empty body for a project lookup. */
export class ProjectNotFoundError extends HandledApiError {
  constructor() {
    super("Project not found")
    this.name = "ProjectNotFoundError"
  }
}

export type ProjectDetails = NonNullable<
  GetProjectInfoResponse["data"]
>["result"]

/** Fetches the user's projects for a filter (owned / member / both / all). */
export const useProjectsQuery = (
  filter: PROJECT_FILTER,
  enabled: boolean = true
): UseQueryResult<ProjectData[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.projects.list(filter),
    enabled,
    meta: { errorTitle: "Error Fetching Projects!" },
    queryFn: async (): Promise<ProjectData[]> => {
      const res = (await getProjects(
        await getAxios(),
        filter
      )) as AxiosResponse<ProjectListResponse>
      if (!handleSequenceError(res.data, "Error Retrieving Projects!")) {
        throw new HandledApiError("Sequence error retrieving projects")
      }
      return res.data.result
    },
  })
}

/** Fetches archived projects. Keep disabled until the archived view is shown. */
export const useArchivedProjectsQuery = (
  enabled: boolean = true
): UseQueryResult<ProjectData[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.projects.archived,
    enabled,
    meta: { errorTitle: "Error Fetching Archived Projects!" },
    queryFn: async (): Promise<ProjectData[]> => {
      const res = (await availableArchivedProjects(
        await getAxios()
      )) as AxiosResponse<ArchivedProjectsResponse>
      if (
        !handleSequenceError(res.data, "Error Retrieving Archived Projects!")
      ) {
        throw new HandledApiError("Sequence error retrieving archived projects")
      }
      // Mark them, so viewFor() knows these live in the Archived view.
      return res.data.result.map((project) => ({ ...project, archived: true }))
    },
  })
}

/** Fetches the user's project folders (grouping paths). */
export const useProjectFoldersQuery = (
  enabled: boolean = true
): UseQueryResult<string[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.projects.folders,
    enabled,
    meta: { errorTitle: "Error Fetching Project Folders!" },
    queryFn: async (): Promise<string[]> => {
      const res = await getProjectFolders(await getAxios())
      // The dlake response wraps payloads in `{ result }`; be defensive in
      // case the shape varies.
      const payload = res.data as string[] | { result?: string[] }
      return Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.result)
        ? payload.result
        : []
    },
  })
}

/**
 * Fetches a single project's details (id, owner, metadata). Throws
 * `ProjectNotFoundError` when the backend returns an empty body — call sites
 * handle that case (notify + redirect); other errors notify globally.
 */
export const useProjectDetailsQuery = (
  id: string | undefined,
  enabled: boolean = true
): UseQueryResult<ProjectDetails> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.projects.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    meta: { errorTitle: "Error Fetching Project Details!" },
    queryFn: async (): Promise<ProjectDetails> => {
      const res = await getProjectInfo(await getAxios(), id as string)
      const data = (res as GetProjectInfoResponse).data
      // A missing body OR missing result means the project doesn't exist
      // (e.g. it was just deleted); never return undefined to react-query.
      if (!data?.result) {
        throw new ProjectNotFoundError()
      }
      return data.result
    },
  })
}

/** Fetches a project's member list (newline-separated on the wire). */
export const useProjectMembersQuery = (
  id: string,
  enabled: boolean = true
): UseQueryResult<string[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.projects.members(id),
    enabled: enabled && Boolean(id),
    meta: { errorTitle: "Error Fetching Project Members!" },
    queryFn: async (): Promise<string[]> => {
      const res = await projectMembers(await getAxios(), id)
      return res.data.split("\n").filter((member) => member.trim() !== "")
    },
  })
}

/**
 * queryFn for a project's type. Sequence-ish errors (Err msg body or
 * empty string) throw a SILENT HandledApiError — historical behavior is to
 * fall back without notifying; transport errors surface via meta/caller.
 */
const projectTypeQueryFn =
  (getAxios: () => Promise<AxiosInstance>, id: string) =>
  async (): Promise<PROJECT_TYPE> => {
    const res = await getProjectType(await getAxios(), id)
    const hasError =
      (typeof res.data === "object" &&
        res.data !== null &&
        "Err msg" in (res.data as object)) ||
      (typeof res.data === "string" && (res.data as string).trim() === "")
    if (hasError) {
      throw new HandledApiError("Sequence error fetching project type")
    }
    return res.data
  }

/** Fetches a project's type declaratively; cached 5 minutes. */
export const useProjectTypeQuery = (
  id: string,
  enabled: boolean = true
): UseQueryResult<PROJECT_TYPE> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.projects.type(id),
    enabled: enabled && Boolean(id),
    staleTime: 5 * 60_000,
    meta: { errorTitle: "Error Fetching Data!" },
    queryFn: projectTypeQueryFn(getAxios, id),
  })
}
